/**
 * Workspace Entitlements
 *
 * Resolves workspace-scoped billing inheritance without changing a user's
 * personal plan.
 *
 * Primary responsibilities:
 * - Resolve a Space's workspace owner plan for entitlement checks
 * - Grant Pro-level workspace features inside Ultra-owned workspaces
 * - Keep workspace sponsorship separate from global account billing
 *
 * Dependencies:
 * - Supabase clients
 * - Losi pricing plan helpers
 */

import type { SupabaseClient } from '@supabase/supabase-js'

import {
  canCreateSpaceObject,
  getEffectiveFeaturePlanId,
  isBusinessPlanId,
  normalizePersonalPlanTier,
  pickHigherPersonalPlan,
  type FeaturePlanProfileLike,
  type LosiPublicPlanId,
  type LosiSpaceObjectType
} from '@/lib/billing/losi-pricing'
import {
  ProductAnalyticsEventNames,
  type ProductAnalyticsSurface
} from '@/lib/analytics/product-events'
import { trackServer } from '@/lib/analytics/track'
import type { Database } from '@/supabase/types'

type WorkspaceEntitlementClient = SupabaseClient<Database>

export type WorkspaceSponsorProfile = FeaturePlanProfileLike & {
  user_id?: string | null
}

type SpaceWorkspaceOwnerRow = {
  user_id: string | null
  workspace_id: string | null
}

const WORKSPACE_ULTRA_SPONSORED_PLAN = 'pro' as const

export const WORKSPACE_ULTRA_INHERITANCE_FEATURE_FLAG =
  'workspace_ultra_inheritance_v1'

const WORKSPACE_ULTRA_INHERITANCE_DEFAULT_ENABLED = true

// --------------

export function isWorkspaceUltraInheritanceEnabled() {
  const configuredValue = [
    process.env.WORKSPACE_ULTRA_INHERITANCE_V1,
    process.env.NEXT_PUBLIC_WORKSPACE_ULTRA_INHERITANCE_V1
  ].find((value) => value?.trim())

  if (!configuredValue) {
    return WORKSPACE_ULTRA_INHERITANCE_DEFAULT_ENABLED
  }

  const normalizedValue = configuredValue.trim().toLowerCase()

  return ['true', '1', 'enabled', 'on'].includes(normalizedValue)
}

// --------------

export function getWorkspaceSponsoredFeaturePlanId(
  profile: FeaturePlanProfileLike | null | undefined,
  workspaceSponsorProfile?: FeaturePlanProfileLike | null,
  options: {
    flagEnabled?: boolean
  } = {}
): LosiPublicPlanId {
  const personalPlanId = getEffectiveFeaturePlanId(profile)
  const flagEnabled =
    options.flagEnabled ?? isWorkspaceUltraInheritanceEnabled()

  if (!flagEnabled) {
    return personalPlanId
  }

  const workspaceSponsorPlanId = getEffectiveFeaturePlanId(
    workspaceSponsorProfile
  )

  if (workspaceSponsorPlanId !== 'ultra') {
    return personalPlanId
  }

  if (
    personalPlanId === 'enterprise' ||
    isBusinessPlanId(personalPlanId)
  ) {
    return personalPlanId
  }

  return pickHigherPersonalPlan(
    normalizePersonalPlanTier(personalPlanId),
    WORKSPACE_ULTRA_SPONSORED_PLAN
  )
}

// --------------

export function canCreateWorkspaceSpaceObject(
  profile: FeaturePlanProfileLike | null | undefined,
  objectType: LosiSpaceObjectType,
  workspaceSponsorProfile?: FeaturePlanProfileLike | null,
  options: {
    flagEnabled?: boolean
  } = {}
) {
  return canCreateSpaceObject(
    getWorkspaceSponsoredFeaturePlanId(profile, workspaceSponsorProfile, options),
    objectType
  )
}

// --------------

export function getWorkspaceEffectivePlanResolution(
  profile: FeaturePlanProfileLike | null | undefined,
  workspaceSponsorProfile?: FeaturePlanProfileLike | null
) {
  const currentPlan = getEffectiveFeaturePlanId(profile)
  const workspacePlan = workspaceSponsorProfile
    ? getEffectiveFeaturePlanId(workspaceSponsorProfile)
    : currentPlan
  const flagEnabled = isWorkspaceUltraInheritanceEnabled()
  const effectivePlan = getWorkspaceSponsoredFeaturePlanId(
    profile,
    workspaceSponsorProfile,
    {
      flagEnabled
    }
  )

  return {
    currentPlan,
    effectivePlan,
    flagEnabled,
    inheritanceApplied: effectivePlan !== currentPlan,
    workspacePlan
  }
}

// --------------

export async function trackWorkspaceEffectivePlanResolved(input: {
  profile: FeaturePlanProfileLike | null | undefined
  source: string
  surface: ProductAnalyticsSurface
  userId: string
  workspaceId?: string | null
  workspaceSponsorProfile?: FeaturePlanProfileLike | null
}) {
  const resolution = getWorkspaceEffectivePlanResolution(
    input.profile,
    input.workspaceSponsorProfile
  )

  await trackServer(ProductAnalyticsEventNames.WORKSPACE_EFFECTIVE_PLAN_RESOLVED, {
    user_id: input.userId,
    current_plan: resolution.currentPlan,
    effective_plan: resolution.effectivePlan,
    workspace_plan: resolution.workspacePlan,
    workspace_id: input.workspaceId ?? undefined,
    surface: input.surface,
    source: input.source,
    feature_flag: WORKSPACE_ULTRA_INHERITANCE_FEATURE_FLAG,
    flag_enabled: resolution.flagEnabled,
    inheritance_applied: resolution.inheritanceApplied,
    variant: resolution.flagEnabled ? 'enabled' : 'inactive'
  })

  return resolution
}

// --------------

async function getSpaceWorkspaceOwner(
  supabase: WorkspaceEntitlementClient,
  spaceId: string
) {
  const { data: space, error: spaceError } = await supabase
    .from('spaces')
    .select('user_id, workspace_id')
    .eq('id', spaceId)
    .maybeSingle()

  if (spaceError) {
    throw spaceError
  }

  const spaceRow = space as SpaceWorkspaceOwnerRow | null

  if (!spaceRow?.workspace_id) {
    return {
      ownerUserId: spaceRow?.user_id ?? null,
      workspaceId: null
    }
  }

  const { data: workspace, error: workspaceError } = await supabase
    .from('workspaces')
    .select('user_id')
    .eq('id', spaceRow.workspace_id)
    .maybeSingle()

  if (workspaceError) {
    throw workspaceError
  }

  return {
    ownerUserId: workspace?.user_id ?? spaceRow.user_id ?? null,
    workspaceId: spaceRow.workspace_id
  }
}

// --------------

export async function getSpaceWorkspaceSponsorProfile(
  supabase: WorkspaceEntitlementClient,
  spaceId: string
): Promise<WorkspaceSponsorProfile | null> {
  const { ownerUserId } = await getSpaceWorkspaceOwner(supabase, spaceId)

  if (!ownerUserId) {
    return null
  }

  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('user_id, plan, nexus_plan, nexus_plan_status')
    .eq('user_id', ownerUserId)
    .maybeSingle()

  if (profileError) {
    throw profileError
  }

  return profile
}

// --------------

export async function getSpaceWorkspaceSponsorContext(
  supabase: WorkspaceEntitlementClient,
  spaceId: string
) {
  const { ownerUserId, workspaceId } = await getSpaceWorkspaceOwner(
    supabase,
    spaceId
  )

  if (!ownerUserId) {
    return {
      sponsorProfile: null,
      workspaceId
    }
  }

  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('user_id, plan, nexus_plan, nexus_plan_status')
    .eq('user_id', ownerUserId)
    .maybeSingle()

  if (profileError) {
    throw profileError
  }

  return {
    sponsorProfile: profile,
    workspaceId
  }
}
